import { isArray } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { animated, useSpring } from '@react-spring/web';
import { hooks } from '@wove/react';
import { ComponentProps, CSSProperties, FC, ReactElement, useRef } from 'react';
import { useEffectCustom, useMutationEffect, useResizeEffect } from '../hooks';
import { usePropsListActiveValueChange } from '../hooks/use-props-list-active-value-change';
import { If } from '../if';
import { IphoneBottomAdaptive } from '../iphone-bottom-adaptive';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { px2DPIpx } from '../utils/pxrem';
import { bound } from './helper/bound';
import './style.less';
import { TabContent } from './tab-content';
import { TabPane } from './tab-pane';

const classPrefix = `v-tabs`;

export type TabsProps = {
  activeKey?: string;
  defaultActiveKey?: string;
  onPreChange?: (key: string) => Promise<void>;
  onChange?: (key: string) => void;
  onTabClick?: (key: string) => void;
  position?: 'top' | 'bottom';
  tabsType?: 'freedom' | 'fixed';
  height?: number | string;
  lazyLoad?: boolean;
  hideActiveLine?: boolean;
  activeLineWidth?: number;
  hideTabsHeader?: boolean;
  hideTabsContent?: boolean;
  adaptiveIphone?: boolean;
  /** 是否显示左右mask，默认值：true */
  leftRightMask?: boolean;
  tabBarExtraContent?: true | ReactElement;
  /** leftRightMask=true 有效 */
  onTabBarExtraClick?: (event) => void;
} & CommonPropsWithChildren;

export type TabsStaticMethods = {
  Pane: typeof TabPane;
};

/**
 * Tabs
 * ```
 * position: headerbar方向
 * tabsType：内容类型
 *   freedom：自由模式
 *   fixed：固定高度模式
 * ```
 * @param props
 * @returns
 */
export const Tabs: FC<TabsProps> & TabsStaticMethods = (props) => {
  const tabListContainerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const position = props.position || 'bottom';
  const tabsType = props.tabsType || 'fixed';
  const activeLineWidth = parseFloat(px2DPIpx(props.activeLineWidth || 0));
  const activatedKeyList = useRef<string[]>([]);

  const onPushActivatedKeyList = hooks.useCallbackRef((key: string) => {
    if (key && !activatedKeyList.current.includes(key)) {
      activatedKeyList.current.push(key);
    }
  });
  const childrenList = isArray(props.children) ? props.children : [props.children];
  const panes = childrenList.filter(Boolean) as ReactElement<ComponentProps<typeof TabPane>>[];

  const [activeKey, setActiveKey] = usePropsListActiveValueChange<string>({
    value: props.activeKey,
    defaultValue: props.defaultActiveKey,
    onChange: props.onChange,
    valueList: panes.map((item) => item.key) as string[],
  });
  if (activeKey) {
    onPushActivatedKeyList(activeKey);
  }

  const [{ x, width }, api] = useSpring(() => ({
    x: 0,
    width: 0,
    config: {
      tension: 300,
      clamp: true,
    },
  }));

  const [{ scrollLeft }, scrollApi] = useSpring(() => ({
    scrollLeft: 0,
    config: {
      tension: 300,
      clamp: true,
    },
  }));

  const [{ leftMaskOpacity, rightMaskOpacity }, maskApi] = useSpring(() => ({
    leftMaskOpacity: 0,
    rightMaskOpacity: 0,
    config: {
      clamp: true,
    },
  }));

  const animate = hooks.useCallbackRef((immediate = false) => {
    const container = tabListContainerRef.current;
    if (!container) return;
    const activeIndex = panes.findIndex((item) => item.key === activeKey);
    if (activeIndex === -1) return;
    const activeTabWrapper = container.children.item(activeIndex) as HTMLDivElement;
    const activeTab = activeTabWrapper.children.item(0) as HTMLDivElement;
    const activeTabLeft = activeTab.offsetLeft;
    const activeTabWidth = activeTab.offsetWidth;

    const containerWidth = container.offsetWidth;
    const containerScrollWidth = container.scrollWidth;

    const containerScrollLeft = container.scrollLeft;

    const x = activeTabLeft;

    const newActiveLineWidth =
      activeLineWidth > 0 && activeLineWidth > activeTabWidth ? activeTabWidth : activeLineWidth;

    api.start({
      x: newActiveLineWidth > 0 ? x + (activeTabWidth - newActiveLineWidth) / 2 : x,
      width: newActiveLineWidth > 0 ? newActiveLineWidth : activeTabWidth,
      immediate,
    });

    const maxScrollDistance = containerScrollWidth - containerWidth;
    if (maxScrollDistance <= 0) return;

    const nextScrollLeft = bound(
      activeTabLeft - (containerWidth - activeTabWidth) / 2,
      0,
      containerScrollWidth - containerWidth,
    );

    scrollApi.start({
      scrollLeft: nextScrollLeft,
      from: { scrollLeft: containerScrollLeft },
      immediate,
    });
  });

  useEffectCustom(() => {
    animate(true);
  }, []);

  hooks.useUpdateEffect(() => {
    animate();
  }, [activeKey]);

  useResizeEffect(() => {
    animate(true);
  }, rootRef);

  useMutationEffect(
    () => {
      animate(true);
    },
    tabListContainerRef,
    {
      subtree: true,
      childList: true,
      characterData: true,
    },
  );

  const updateMask = hooks.useThrottleCallback((immediate = false) => {
    const container = tabListContainerRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const showLeftMask = scrollLeft > 0;
    const showRightMask = scrollLeft + container.offsetWidth < container.scrollWidth;

    maskApi.start({
      leftMaskOpacity: showLeftMask ? 1 : 0,
      rightMaskOpacity: showRightMask ? 1 : 0,
      immediate,
    });
  });

  const onTabHeaderScroll = hooks.useCallbackRef(() => {
    updateMask();
  });

  useEffectCustom(() => {
    updateMask(true);
  }, []);

  const height = tabsType === 'fixed' ? props.height || '100%' : props.height;
  const dpiHeight = typeof height === 'number' ? px2DPIpx(height) : height;
  const style = {} as CSSProperties;
  if (dpiHeight) {
    style.height = dpiHeight;
  }

  const onTabClick = hooks.useCallbackRef(async (pane) => {
    const { key } = pane;
    if (pane.props.disabled) return;
    if (key === undefined || key === null) {
      return;
    }
    if (key === activeKey) {
      props.onTabClick?.(key);
      return;
    }
    if (props.onPreChange) {
      await props.onPreChange(key.toString());
    }
    onPushActivatedKeyList(key.toString());
    setActiveKey(key.toString());
    props.onTabClick?.(key);
  });

  return withNativeProps(
    props,
    <div
      className={classNames(classPrefix, `${classPrefix}-${tabsType}`, `${classPrefix}-${position}`)}
      ref={rootRef}
      style={style}
    >
      <If isTrue={position === 'bottom' && !props.hideTabsContent}>
        <TabContent
          panes={panes}
          activeKey={activeKey}
          tabsType={tabsType}
          lazyLoad={props.lazyLoad}
          activatedKeyList={activatedKeyList.current}
        />
      </If>
      <If isTrue={!props.hideTabsHeader}>
        <div className={`${classPrefix}-header-wrapper`}>
          <div className={`${classPrefix}-header`}>
            <animated.div
              className={`${classPrefix}-list`}
              ref={tabListContainerRef}
              scrollLeft={scrollLeft}
              onScroll={onTabHeaderScroll}
            >
              {panes.map((pane) =>
                withNativeProps(
                  pane.props,
                  <div key={pane.key} className={`${classPrefix}-tab-wrapper`}>
                    <div
                      onClick={onTabClick.bind(null, pane)}
                      className={classNames(`${classPrefix}-tab`, {
                        [`${classPrefix}-tab-active`]: pane.key === activeKey,
                        [`${classPrefix}-tab-disabled`]: pane.props.disabled,
                      })}
                    >
                      {pane.props.title}
                    </div>
                  </div>,
                ),
              )}

              {!props.hideActiveLine ? (
                <animated.div
                  className={`${classPrefix}-tab-line`}
                  style={{
                    width,
                    x,
                  }}
                />
              ) : null}
            </animated.div>
            {props.leftRightMask && (
              <animated.div
                className={classNames(`${classPrefix}-header-mask`, `${classPrefix}-header-mask-left`)}
                style={{
                  opacity: leftMaskOpacity,
                }}
              />
            )}
            {props.leftRightMask && (
              <animated.div
                className={classNames(`${classPrefix}-header-mask`, `${classPrefix}-header-mask-right`)}
                style={{
                  opacity: rightMaskOpacity,
                }}
              />
            )}
          </div>
          {props.tabBarExtraContent === true ? (
            <div className={`${classPrefix}-tabbar-extra`} onClick={props.onTabBarExtraClick}></div>
          ) : (
            props.tabBarExtraContent
          )}
          {props.adaptiveIphone ? <IphoneBottomAdaptive /> : null}
        </div>
      </If>
      <If isTrue={position === 'top' && !props.hideTabsContent}>
        <TabContent
          panes={panes}
          activeKey={activeKey}
          tabsType={tabsType}
          lazyLoad={props.lazyLoad}
          activatedKeyList={activatedKeyList.current}
        />
      </If>
    </div>,
  );
};
Tabs.Pane = TabPane;

Tabs.defaultProps = {
  lazyLoad: false,
  position: 'bottom',
  tabsType: 'fixed',
  leftRightMask: true,
};
